import { useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import {
  OptimizelyContext,
  useDecision as nativeUseDecision,
} from '@optimizely/react-sdk';
import { useAnalyticsStore } from 'stores/analytics';
import { getDecisionDetails } from './parser';
import Bugsnag from 'lib/bugsnag';
import { getCookie } from 'lib/cookies/cookieHelper';
import { forceVariation } from './forceVariation';
import { USER_ID_COOKIE_NAME } from '../getUserId';
import { logForDev } from 'lib/logging/devLogger';
import { track } from 'lib/tracking/track';
import { TrackingEvent } from 'lib/tracking/events';
import { isTestingUserAgent } from 'lib/e2e-test-helpers/isTestingUserAgent';
import { Variation } from '../flagConfigs';

export default function useDecision(
  ...args: Parameters<typeof nativeUseDecision>
): ReturnType<typeof nativeUseDecision> {
  const userAgent =
    typeof window !== 'undefined' ? window.navigator.userAgent : '';
  const isE2ETest = isTestingUserAgent(userAgent);

  // default to auto-update in order to allow updating user id
  // this is relevant for static pages that navigate to an optimizely-enabled page
  args[1] = { autoUpdate: true, ...args[1] };
  const { optimizely } = useContext(OptimizelyContext);
  const router = useRouter();
  const urlParams = new URLSearchParams(
    router.isReady ? (router.query as Record<string, string>) : {},
  );
  const optimizelyUserId = getCookie(USER_ID_COOKIE_NAME);

  forceVariation(urlParams, optimizely);

  const [decision, ...rest] = nativeUseDecision(...args);
  const isAnalyticsReady = useAnalyticsStore((s) => s.isAnalyticsReady);
  // TODO refactor, too long
  useEffect(() => {
    const isOptimizelyEnabled =
      decision.enabled && isAnalyticsReady && optimizely;

    // Only track enabled experiments
    if (!isE2ETest && isOptimizelyEnabled) {
      try {
        // get properties needed to forward event from Segment to Optimizely
        const datafile = JSON.parse(
          optimizely.getOptimizelyConfig()?.getDatafile() || '{}',
        );
        const decisionDetails = getDecisionDetails(datafile, {
          ruleKey: decision.ruleKey!,
          variationKey: decision.variationKey!,
        });
        let eventProperties: { [k: string]: any } = {
          experiment_name: decision.ruleKey,
          variation_name: decision.variationKey,
          ...decisionDetails,
        };
        if (!optimizelyUserId) {
          throw new Error('Optimizely user id not found');
        }

        const optimizelyContext: any = { userId: optimizelyUserId };
        if (optimizely?.user?.attributes) {
          optimizelyContext['userAttributes'] = optimizely.user.attributes;
        } else {
          logForDev('Optimizely: no user attributes');
          Bugsnag.notify(new Error('no attributes for Optimizely user'));
        }

        track(TrackingEvent.OPTIMIZELY_EXPERIMENT_VIEWED, eventProperties, {
          context: { Optimizely: optimizelyContext },
        });
      } catch (e: any) {
        logForDev('error on decision event: ', e);
        Bugsnag.notify(e);
      }
    }
  }, [decision, isAnalyticsReady, optimizely, optimizelyUserId, isE2ETest]);

  // we harshly bail out of optimizely for e2e tests and return a choice for control
  if (isE2ETest) {
    const fakeDecision = {
      enabled: false,
      flagKey: 'Optimizely disabled',
      reasons: [],
      ruleKey: 'Optimizely disabled',
      userContext: { id: optimizelyUserId || null, attributes: {} },
      variables: {},
      variationKey: Variation.CONTROL,
    };
    return [fakeDecision, true, true];
  }

  return [decision, ...rest];
}
